import type { ApiClient } from "../../api";
import * as sdk from "../../api/generated";
import { idempotencyHeaders } from "../../api/idempotency";
import {
  deleteApiV1DocumentWorkflowDocumentFilesByFile,
  deleteApiV1DocumentWorkflowDocumentVersionsByVersionLink,
  getApiV1DocumentWorkflowCategories,
  getApiV1DocumentWorkflowCounterparties,
  getApiV1DocumentWorkflowDocuments,
  getApiV1DocumentWorkflowDocumentsByDocument,
  getApiV1DocumentWorkflowDocumentsByDocumentAssignments,
  getApiV1DocumentWorkflowDocumentsByDocumentReads,
  getApiV1DocumentWorkflowDocumentsByDocumentRelations,
  getApiV1DocumentWorkflowDocumentsByDocumentScopes,
  getApiV1DocumentWorkflowDocumentsByDocumentTimeline,
  getApiV1DocumentWorkflowDocumentsByDocumentMailMessages,
  getApiV1DocumentWorkflowDocumentsByDocumentMailThreads,
  getApiV1DocumentWorkflowFolders,
  getApiV1DocumentWorkflowDashboard,
  getApiV1DocumentWorkflowMailMessagesByMessage,
  getApiV1DocumentWorkflowDocumentsByDocumentFiles,
  getApiV1DocumentWorkflowDocumentVersionsByVersionDownload,
  getApiV1DocumentWorkflowDocumentVersionsByVersionPreview,
  getApiV1DocumentWorkflowMailMessagesByMessageAttachments,
  getApiV1DocumentWorkflowMailAttachmentsByAttachmentDownload,
  getApiV1DocumentWorkflowMailAttachmentsByAttachmentPreview,
  postApiV1DocumentWorkflowDocumentsByDocumentFiles,
  postApiV1DocumentWorkflowDocumentsByDocumentFileLinks,
  getApiV1DocumentWorkflowMailOutboxByOutbox,
  getApiV1DocumentWorkflowReportsDeadlines,
  getApiV1DocumentWorkflowReportsExecutors,
  getApiV1FilesByFileDownload,
  postApiV1DocumentWorkflowDocuments,
  patchApiV1DocumentWorkflowDocumentsByDocument,
  postApiV1DocumentWorkflowDocumentsByDocumentAssignments,
  postApiV1DocumentWorkflowDocumentsByDocumentCancel,
  postApiV1DocumentWorkflowDocumentsByDocumentComplete,
  postApiV1DocumentWorkflowDocumentsByDocumentHistoryExports,
  postApiV1DocumentWorkflowDocumentsByDocumentRegister,
  postApiV1DocumentWorkflowDocumentsByDocumentReopen,
  postApiV1DocumentWorkflowDocumentsByDocumentRelations,
  postApiV1DocumentWorkflowDocumentsByDocumentStart,
  postApiV1DocumentWorkflowDocumentsByDocumentReplies,
  postApiV1DocumentWorkflowDocumentsByDocumentRead,
  postApiV1DocumentWorkflowMailboxesByMailboxMessages,
  deleteApiV1DocumentWorkflowMailMessagesByMessageDocumentLinksByDocument,
  postApiV1DocumentWorkflowMailMessagesByMessageDocumentLinks,
  postApiV1DocumentWorkflowMailMessagesByMessageRead,
  postApiV1DocumentWorkflowMailOutboxByOutboxCancel,
  postApiV1DocumentWorkflowMailOutboxByOutboxConfirmRetry,
  postApiV1DocumentWorkflowMailOutboxByOutboxRetry,
  postApiV1DocumentWorkflowMailThreadsByThreadMerge,
  postApiV1DocumentWorkflowMailThreadsByThreadRead,
  postApiV1DocumentWorkflowMailThreadsByThreadSplit,
  putApiV1DocumentWorkflowDocumentsByDocumentControl,
  putApiV1DocumentWorkflowDocumentsByDocumentScopesByScope,
  type GetApiV1DocumentWorkflowCategoriesSuccess,
  type GetApiV1DocumentWorkflowCounterpartiesSuccess,
  type GetApiV1DocumentWorkflowFoldersSuccess,
  type GetApiV1DocumentWorkflowDashboardQuery,
  type GetApiV1DocumentWorkflowDashboardSuccess,
  type GetApiV1DocumentWorkflowDocumentsQuery,
  type GetApiV1DocumentWorkflowDocumentsSuccess,
  type GetApiV1DocumentWorkflowDocumentsByDocumentMailMessagesSuccess,
  type GetApiV1DocumentWorkflowReportsDeadlinesQuery,
  type GetApiV1DocumentWorkflowReportsDeadlinesSuccess,
  type GetApiV1DocumentWorkflowReportsExecutorsQuery,
  type GetApiV1DocumentWorkflowReportsExecutorsSuccess,
  type PostApiV1DocumentWorkflowDocumentsBody,
  type PatchApiV1DocumentWorkflowDocumentsByDocumentBody,
  type PostApiV1DocumentWorkflowDocumentsByDocumentRepliesBody,
  type PostApiV1DocumentWorkflowMailboxesByMailboxMessagesBody,
} from "../../api/generated";

export type DocumentWorkflowDocument =
  GetApiV1DocumentWorkflowDocumentsSuccess["data"][number];
export type DocumentWorkflowDocumentsQuery =
  NonNullable<GetApiV1DocumentWorkflowDocumentsQuery>;
export type DocumentWorkflowDashboard =
  GetApiV1DocumentWorkflowDashboardSuccess["data"];
export type DocumentWorkflowDashboardQuery =
  NonNullable<GetApiV1DocumentWorkflowDashboardQuery>;
export type DocumentWorkflowExecutorReport =
  GetApiV1DocumentWorkflowReportsExecutorsSuccess["data"];
export type DocumentWorkflowExecutorReportQuery =
  NonNullable<GetApiV1DocumentWorkflowReportsExecutorsQuery>;
export type DocumentWorkflowDeadlineReport =
  GetApiV1DocumentWorkflowReportsDeadlinesSuccess["data"];
export type DocumentWorkflowDeadlineReportQuery =
  NonNullable<GetApiV1DocumentWorkflowReportsDeadlinesQuery>;
export type DocumentWorkflowCategory =
  GetApiV1DocumentWorkflowCategoriesSuccess["data"][number];
export type DocumentWorkflowFolder =
  GetApiV1DocumentWorkflowFoldersSuccess["data"][number];
export type DocumentWorkflowCounterparty =
  GetApiV1DocumentWorkflowCounterpartiesSuccess["data"][number];
export type DocumentWorkflowDocumentInput =
  PostApiV1DocumentWorkflowDocumentsBody;
export type DocumentWorkflowMailMessage =
  GetApiV1DocumentWorkflowDocumentsByDocumentMailMessagesSuccess["data"][number];
export interface DocumentWorkflowMailAttachment {
  id: string;
  file_id: string;
  name: string;
  safe_name: string;
  mime_type: string;
  size: number;
  sha256: string;
  scan_status: string;
  provenance?: {
    type: "email_attachment";
    reference: { mail_message_id: string; attachment_id: string };
  };
  available_actions?: string[];
}

export type DocumentWorkflowMailMessageDetail =
  sdk.GetApiV1DocumentWorkflowMailMessagesByMessageSuccess["data"];
export type DocumentWorkflowFileVersion =
  sdk.GetApiV1DocumentWorkflowDocumentVersionsByVersionSuccess["data"];
export type DocumentWorkflowSignatureCapabilities =
  sdk.GetApiV1DocumentWorkflowSignatureCapabilitiesSuccess["data"];
export type DocumentWorkflowSignatureRequest =
  sdk.PostApiV1DocumentWorkflowDocumentVersionsByVersionSignatureRequestsSuccess["data"];
export type DocumentWorkflowSignature =
  sdk.PostApiV1DocumentWorkflowSignatureRequestsBySignatureRequestCompleteSuccess["data"];
export type DocumentWorkflowSignatureVerification =
  sdk.PostApiV1DocumentWorkflowSignaturesBySignatureVerifySuccess["data"];
export type DocumentWorkflowSignatureProfile =
  DocumentWorkflowSignatureRequest["cades_profile"];
export type DocumentWorkflowMailAddress = NonNullable<
  DocumentWorkflowMailMessageDetail["from"]
>;
export type DocumentWorkflowMailThread =
  sdk.GetApiV1DocumentWorkflowDocumentsByDocumentMailThreadsSuccess["data"][number];
export type DocumentWorkflowOutbox =
  sdk.GetApiV1DocumentWorkflowMailOutboxByOutboxSuccess["data"];

export class DocumentWorkflowApi {
  constructor(private readonly client: ApiClient) {}

  async participants(id: string) {
    return (
      await sdk.getApiV1DocumentWorkflowDocumentsByDocumentParticipants(
        this.client,
        { path: { document: id } },
      )
    ).data;
  }
  async participantCandidates(id: string) {
    return (
      await sdk.getApiV1DocumentWorkflowDocumentsByDocumentParticipantCandidates(
        this.client,
        { path: { document: id } },
      )
    ).data;
  }
  async replaceRecipients(id: string, recipient_ids: string[]) {
    return (
      await sdk.putApiV1DocumentWorkflowDocumentsByDocumentRecipients(
        this.client,
        {
          path: { document: id },
          body: { recipient_ids },
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }
  async addParticipant(id: string, user_id: string) {
    return (
      await sdk.postApiV1DocumentWorkflowDocumentsByDocumentParticipants(
        this.client,
        {
          path: { document: id },
          body: { user_id, role: "participant" },
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }
  async removeParticipant(id: string, participant: string) {
    await sdk.deleteApiV1DocumentWorkflowDocumentsByDocumentParticipantsByParticipant(
      this.client,
      { path: { document: id, participant } },
    );
  }
  async removeAssignment(id: string) {
    await sdk.deleteApiV1DocumentWorkflowDocumentsByDocumentAssignments(
      this.client,
      { path: { document: id } },
    );
  }
  async removeControl(id: string) {
    await sdk.deleteApiV1DocumentWorkflowDocumentsByDocumentControl(
      this.client,
      { path: { document: id } },
    );
  }
  async revokeScope(id: string, scope: string) {
    await sdk.deleteApiV1DocumentWorkflowDocumentsByDocumentScopesByScope(
      this.client,
      { path: { document: id, scope } },
    );
  }
  async deleteRelation(relation: string) {
    await sdk.deleteApiV1DocumentWorkflowRelationsByRelation(this.client, {
      path: { relation },
    });
  }
  async historyExportStatus(id: string) {
    return (
      await sdk.getApiV1DocumentWorkflowHistoryExportsByExport(this.client, {
        path: { export: id },
      })
    ).data;
  }
  async historyExportDownload(id: string) {
    return (
      await sdk.getApiV1DocumentWorkflowHistoryExportsByExportDownload(
        this.client,
        { path: { export: id } },
      )
    ).data;
  }
  async comments(id: string) {
    return (
      await sdk.getApiV1DocumentWorkflowDocumentsByDocumentComments(
        this.client,
        { path: { document: id } },
      )
    ).data;
  }
  /** Сообщение в обсуждение с серверными UUID ответа, упоминаний и файлов. */
  async addComment(
    id: string,
    text: string,
    replyTo?: string | null,
    mentionedUserIds: string[] = [],
    attachmentFileIds: string[] = [],
  ) {
    return (
      await sdk.postApiV1DocumentWorkflowDocumentsByDocumentComments(
        this.client,
        {
          path: { document: id },
          body: {
            // Сообщение из одного файла уходит без текста: пустую строку
            // сервер не примет, а отсутствие поля с вложением — да.
            ...(text ? { text } : {}),
            ...(replyTo ? { reply_to_comment_id: replyTo } : {}),
            ...(mentionedUserIds.length
              ? { mentioned_user_ids: mentionedUserIds }
              : {}),
            ...(attachmentFileIds.length
              ? { attachment_file_ids: attachmentFileIds }
              : {}),
          },
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }
  async updateComment(
    id: string,
    text: string,
    mentionedUserIds: string[] = [],
  ) {
    return (
      await sdk.patchApiV1DocumentWorkflowCommentsByComment(this.client, {
        path: { comment: id },
        body: { text, mentioned_user_ids: mentionedUserIds },
      })
    ).data;
  }
  async deleteComment(id: string) {
    await sdk.deleteApiV1DocumentWorkflowCommentsByComment(this.client, {
      path: { comment: id },
    });
  }
  /**
   * Отметка прочтения обсуждения.
   *
   * `throughCommentId` — последнее сообщение, которое человек долистал и
   * увидел. Без него прочитанным становится всё обсуждение целиком.
   */
  async readDiscussion(id: string, throughCommentId?: string) {
    return sdk.postApiV1DocumentWorkflowDocumentsByDocumentDiscussionRead(
      this.client,
      {
        path: { document: id },
        body: throughCommentId ? { comment_id: throughCommentId } : {},
        headers: idempotencyHeaders(),
      },
    );
  }
  timelinePage(id: string, cursor?: string) {
    return sdk.getApiV1DocumentWorkflowDocumentsByDocumentTimeline(
      this.client,
      { path: { document: id }, query: { cursor, per_page: 50 } },
    );
  }

  documents(
    mailboxId: string,
    page = 1,
    unread?: boolean,
    type: "incoming" | "outgoing" = "incoming",
  ) {
    return getApiV1DocumentWorkflowDocuments(this.client, {
      query: {
        mailbox_id: mailboxId,
        type,
        page,
        per_page: 100,
        sort: "-received_at",
        ...(unread === undefined ? {} : { unread }),
      },
    });
  }

  listDocuments(query: DocumentWorkflowDocumentsQuery) {
    return getApiV1DocumentWorkflowDocuments(this.client, { query });
  }

  async document(id: string): Promise<DocumentWorkflowDocument> {
    return (
      await getApiV1DocumentWorkflowDocumentsByDocument(this.client, {
        path: { document: id },
      })
    ).data;
  }

  // Создание документа вручную. Ручка требует Idempotency-Key, поэтому ключ
  // формируем здесь же, рядом с остальными пишущими операциями модуля.
  async createDocument(
    input: DocumentWorkflowDocumentInput,
  ): Promise<DocumentWorkflowDocument> {
    return (
      await postApiV1DocumentWorkflowDocuments(this.client, {
        body: input,
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async updateDocument(
    id: string,
    input: PatchApiV1DocumentWorkflowDocumentsByDocumentBody,
  ) {
    return (
      await patchApiV1DocumentWorkflowDocumentsByDocument(this.client, {
        path: { document: id },
        body: input,
        headers: idempotencyHeaders(),
      })
    ).data;
  }
  async register(
    id: string,
    input: {
      journal?: string;
      number?: string | null;
      registration_date?: string | null;
      incoming_number?: string | null;
      incoming_date?: string | null;
    },
  ) {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentRegister(this.client, {
        path: { document: id },
        body: input,
        headers: idempotencyHeaders(),
      })
    ).data;
  }
  async assign(
    id: string,
    input: { assignee_id: string; reason?: string | null },
  ) {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentAssignments(
        this.client,
        { path: { document: id }, body: input, headers: idempotencyHeaders() },
      )
    ).data;
  }
  async setControl(
    id: string,
    input: {
      controller_id: string;
      due_at: string;
      priority?: "low" | "normal" | "high" | "critical";
      reason?: string | null;
    },
  ) {
    return (
      await putApiV1DocumentWorkflowDocumentsByDocumentControl(this.client, {
        path: { document: id },
        body: input,
        headers: idempotencyHeaders(),
      })
    ).data;
  }
  async lifecycle(
    id: string,
    action: "start" | "complete" | "reopen" | "cancel",
    reason: string,
    outcome?: string,
    override = false,
  ) {
    const request = { path: { document: id }, headers: idempotencyHeaders() };
    if (action === "start")
      return (
        await postApiV1DocumentWorkflowDocumentsByDocumentStart(this.client, {
          ...request,
          body: { reason },
        })
      ).data;
    if (action === "complete")
      return (
        await postApiV1DocumentWorkflowDocumentsByDocumentComplete(
          this.client,
          {
            ...request,
            body: { outcome: outcome ?? reason, reason, override },
          },
        )
      ).data;
    if (action === "reopen")
      return (
        await postApiV1DocumentWorkflowDocumentsByDocumentReopen(this.client, {
          ...request,
          body: { reason },
        })
      ).data;
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentCancel(this.client, {
        ...request,
        body: { reason },
      })
    ).data;
  }
  async assignments(id: string) {
    return (
      await getApiV1DocumentWorkflowDocumentsByDocumentAssignments(
        this.client,
        { path: { document: id } },
      )
    ).data;
  }
  async reads(id: string) {
    return (
      await getApiV1DocumentWorkflowDocumentsByDocumentReads(this.client, {
        path: { document: id },
      })
    ).data;
  }
  async relations(id: string) {
    return (
      await getApiV1DocumentWorkflowDocumentsByDocumentRelations(this.client, {
        path: { document: id },
      })
    ).data;
  }
  async timeline(id: string) {
    return (
      await getApiV1DocumentWorkflowDocumentsByDocumentTimeline(this.client, {
        path: { document: id },
      })
    ).data;
  }
  async scopes(id: string) {
    return (
      await getApiV1DocumentWorkflowDocumentsByDocumentScopes(this.client, {
        path: { document: id },
      })
    ).data;
  }
  async shareScope(
    id: string,
    scopeId: string,
    accessLevel: "read" | "write" | "manage",
  ) {
    return (
      await putApiV1DocumentWorkflowDocumentsByDocumentScopesByScope(
        this.client,
        {
          path: { document: id, scope: scopeId },
          body: { access_level: accessLevel },
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }
  async createRelation(
    id: string,
    input: {
      related_document_id: string;
      type: "related" | "parent" | "child" | "duplicate" | "response_to" | "answered_by";
      note?: string | null;
    },
  ) {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentRelations(this.client, {
        path: { document: id },
        body: input,
        headers: idempotencyHeaders(),
      })
    ).data;
  }
  async requestHistoryExport(id: string, format: "json" | "csv" = "json") {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentHistoryExports(
        this.client,
        {
          path: { document: id },
          body: { format },
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }

  // Справочники для формы создания: рубрики, папки и контрагенты.
  async categories(): Promise<DocumentWorkflowCategory[]> {
    return (await getApiV1DocumentWorkflowCategories(this.client, {})).data;
  }

  async folders(): Promise<DocumentWorkflowFolder[]> {
    return (await getApiV1DocumentWorkflowFolders(this.client, {})).data;
  }

  async counterparties(): Promise<DocumentWorkflowCounterparty[]> {
    return (
      await getApiV1DocumentWorkflowCounterparties(this.client, {
        query: { per_page: 100 },
      })
    ).data;
  }

  dashboard(query: DocumentWorkflowDashboardQuery = {}) {
    return getApiV1DocumentWorkflowDashboard(this.client, { query });
  }

  executorReport(query: DocumentWorkflowExecutorReportQuery = {}) {
    return getApiV1DocumentWorkflowReportsExecutors(this.client, { query });
  }

  deadlineReport(query: DocumentWorkflowDeadlineReportQuery = {}) {
    return getApiV1DocumentWorkflowReportsDeadlines(this.client, { query });
  }

  messages(documentId: string, cursor?: string) {
    return getApiV1DocumentWorkflowDocumentsByDocumentMailMessages(
      this.client,
      {
        path: { document: documentId },
        query: { per_page: 100, cursor },
      },
    );
  }

  async threads(documentId: string): Promise<DocumentWorkflowMailThread[]> {
    const response =
      await getApiV1DocumentWorkflowDocumentsByDocumentMailThreads(
        this.client,
        { path: { document: documentId } },
      );
    return response.data;
  }

  async message(id: string): Promise<DocumentWorkflowMailMessageDetail> {
    return (
      await getApiV1DocumentWorkflowMailMessagesByMessage(this.client, {
        path: { message: id },
      })
    ).data;
  }

  async attachments(id: string): Promise<DocumentWorkflowMailAttachment[]> {
    const response =
      await getApiV1DocumentWorkflowMailMessagesByMessageAttachments(
        this.client,
        { path: { message: id } },
      );

    return response.data as unknown as DocumentWorkflowMailAttachment[];
  }

  /** Вложения документа: имя, размер, тип и доступные действия. */
  async documentFiles(documentId: string) {
    const response = await getApiV1DocumentWorkflowDocumentsByDocumentFiles(
      this.client,
      { path: { document: documentId } },
    );

    return response.data;
  }

  /** Прикрепить к документу уже загруженный и проверенный файл. */
  async attachFile(
    documentId: string,
    input: { confirmed_file_id: string; display_name: string },
  ) {
    const response = await postApiV1DocumentWorkflowDocumentsByDocumentFiles(
      this.client,
      {
        path: { document: documentId },
        body: { ...input, comment: null },
        headers: idempotencyHeaders(),
      },
    );

    return response.data;
  }

  /** Удалить логическое вложение, если это сделал текущий пользователь или системный администратор. */
  deleteDocumentFile(fileId: string) {
    return deleteApiV1DocumentWorkflowDocumentFilesByFile(this.client, {
      path: { file: fileId },
    });
  }

  async renameDocumentFile(fileId: string, displayName: string) {
    const response = await sdk.patchApiV1DocumentWorkflowDocumentFilesByFile(
      this.client,
      { path: { file: fileId }, body: { display_name: displayName } },
    );

    return response.data;
  }

  /** Логический файл документа с актуальной версией. */
  async documentFile(fileId: string) {
    return (
      await sdk.getApiV1DocumentWorkflowDocumentFilesByFile(this.client, {
        path: { file: fileId },
      })
    ).data;
  }

  /** История версий файла от новой к старой. */
  async fileVersions(fileId: string) {
    return (
      await sdk.getApiV1DocumentWorkflowDocumentFilesByFileVersions(
        this.client,
        { path: { file: fileId } },
      )
    ).data;
  }

  /**
   * Новая неизменяемая версия уже подтверждённого файла.
   *
   * `base_version_id` — версия, которую человек видел, начиная операцию: если
   * с тех пор появилась другая, сервер отвечает конфликтом, а не затирает её.
   */
  async createFileVersion(
    fileId: string,
    input: {
      confirmed_file_id: string;
      base_version_id: string;
      comment?: string | null;
    },
  ) {
    return (
      await sdk.postApiV1DocumentWorkflowDocumentFilesByFileVersions(
        this.client,
        {
          path: { file: fileId },
          body: input,
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }

  async version(versionId: string): Promise<DocumentWorkflowFileVersion> {
    return (
      await sdk.getApiV1DocumentWorkflowDocumentVersionsByVersion(this.client, {
        path: { version: versionId },
      })
    ).data;
  }

  /** Что сервер разрешает по подписи: включена ли она и чем проверяется. */
  async signatureCapabilities(): Promise<DocumentWorkflowSignatureCapabilities> {
    return (
      await sdk.getApiV1DocumentWorkflowSignatureCapabilities(this.client, {})
    ).data;
  }

  /** Запрос подписи конкретной версии: сервер выдаёт одноразовый nonce. */
  async createSignatureRequest(
    versionId: string,
    profile: DocumentWorkflowSignatureProfile,
  ): Promise<DocumentWorkflowSignatureRequest> {
    return (
      await sdk.postApiV1DocumentWorkflowDocumentVersionsByVersionSignatureRequests(
        this.client,
        {
          path: { version: versionId },
          body: { cades_profile: profile },
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }

  async signatureRequest(requestId: string) {
    return (
      await sdk.getApiV1DocumentWorkflowSignatureRequestsBySignatureRequest(
        this.client,
        { path: { signatureRequest: requestId } },
      )
    ).data;
  }

  /** Отправка готовой отделённой подписи; сервер сам её проверяет. */
  async completeSignatureRequest(
    requestId: string,
    input: {
      nonce: string;
      detached_signature: string;
      client_version?: string | null;
    },
  ): Promise<DocumentWorkflowSignature> {
    return (
      await sdk.postApiV1DocumentWorkflowSignatureRequestsBySignatureRequestComplete(
        this.client,
        {
          path: { signatureRequest: requestId },
          body: input,
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }

  async versionSignatures(
    versionId: string,
  ): Promise<DocumentWorkflowSignature[]> {
    return (
      await sdk.getApiV1DocumentWorkflowDocumentVersionsByVersionSignatures(
        this.client,
        { path: { version: versionId } },
      )
    ).data;
  }

  async signature(signatureId: string): Promise<DocumentWorkflowSignature> {
    return (
      await sdk.getApiV1DocumentWorkflowSignaturesBySignature(this.client, {
        path: { signature: signatureId },
      })
    ).data;
  }

  /** Повторная серверная проверка уже сохранённой подписи. */
  async verifySignature(
    signatureId: string,
  ): Promise<DocumentWorkflowSignatureVerification> {
    return (
      await sdk.postApiV1DocumentWorkflowSignaturesBySignatureVerify(
        this.client,
        { path: { signature: signatureId }, headers: idempotencyHeaders() },
      )
    ).data;
  }

  /** Файл отделённой подписи: его кладут рядом с документом у получателя. */
  async signatureFile(signatureId: string) {
    return (
      await sdk.getApiV1DocumentWorkflowSignaturesBySignatureFile(this.client, {
        path: { signature: signatureId },
      })
    ).data;
  }

  /** Заказать печатную копию версии с отметкой об электронной подписи. */
  async requestSignatureStamp(versionId: string) {
    return (
      await sdk.postApiV1DocumentWorkflowDocumentVersionsByVersionSignatureStamps(
        this.client,
        { path: { version: versionId }, headers: idempotencyHeaders() },
      )
    ).data;
  }

  async signatureStamp(stampId: string) {
    return (
      await sdk.getApiV1DocumentWorkflowSignatureStampsByStamp(this.client, {
        path: { stamp: stampId },
      })
    ).data;
  }

  /** Подписанная ссылка на готовую печатную копию. */
  async downloadSignatureStamp(stampId: string) {
    return (
      await sdk.getApiV1DocumentWorkflowSignatureStampsByStampDownload(
        this.client,
        { path: { stamp: stampId } },
      )
    ).data;
  }

  /** Подписанная ссылка на предпросмотр версии вложения. */
  async previewVersion(versionId: string) {
    return (
      await getApiV1DocumentWorkflowDocumentVersionsByVersionPreview(
        this.client,
        {
          path: { version: versionId },
        },
      )
    ).data;
  }

  /** Подписанная ссылка на скачивание версии вложения. */
  async downloadVersion(versionId: string) {
    return (
      await getApiV1DocumentWorkflowDocumentVersionsByVersionDownload(
        this.client,
        {
          path: { version: versionId },
        },
      )
    ).data;
  }

  async download(fileId: string) {
    return (
      await getApiV1FilesByFileDownload(this.client, {
        path: { file: fileId },
      })
    ).data;
  }

  async downloadMailAttachment(attachmentId: string) {
    return (
      await getApiV1DocumentWorkflowMailAttachmentsByAttachmentDownload(
        this.client,
        { path: { attachment: attachmentId } },
      )
    ).data;
  }

  /** Подписанная ссылка на предпросмотр вложения письма. */
  async previewMailAttachment(attachmentId: string) {
    return (
      await getApiV1DocumentWorkflowMailAttachmentsByAttachmentPreview(
        this.client,
        { path: { attachment: attachmentId } },
      )
    ).data;
  }

  /**
   * Добавить в файлы документа уже подтверждённый файл, например вложение
   * внутреннего обсуждения. Байты не копируются: связь указывает на тот же
   * объект хранилища.
   */
  async linkFile(documentId: string, fileId: string, displayName?: string) {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentFileLinks(this.client, {
        path: { document: documentId },
        body: {
          source_type: "file",
          source_id: fileId,
          ...(displayName ? { display_name: displayName } : {}),
        },
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async linkMailAttachment(
    documentId: string,
    attachmentId: string,
    target?: { documentFileId?: string; displayName?: string },
  ) {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentFileLinks(this.client, {
        path: { document: documentId },
        body: {
          source_type: "mail_attachment",
          source_id: attachmentId,
          ...(target?.documentFileId
            ? { document_file_id: target.documentFileId }
            : {}),
          ...(target?.displayName ? { display_name: target.displayName } : {}),
        },
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async unlinkDocumentVersion(versionId: string) {
    await deleteApiV1DocumentWorkflowDocumentVersionsByVersionLink(
      this.client,
      {
        path: { version: versionId },
      },
    );
  }

  async outbox(id: string): Promise<DocumentWorkflowOutbox> {
    return (
      await getApiV1DocumentWorkflowMailOutboxByOutbox(this.client, {
        path: { outbox: id },
      })
    ).data;
  }

  async markMessageRead(id: string) {
    return (
      await postApiV1DocumentWorkflowMailMessagesByMessageRead(this.client, {
        path: { message: id },
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async markThreadRead(id: string) {
    return (
      await postApiV1DocumentWorkflowMailThreadsByThreadRead(this.client, {
        path: { thread: id },
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async retryOutbox(
    id: string,
    reason: string,
    confirmed = false,
  ): Promise<DocumentWorkflowOutbox> {
    const request = {
      path: { outbox: id },
      body: { reason },
      headers: idempotencyHeaders(),
    };
    const response = confirmed
      ? await postApiV1DocumentWorkflowMailOutboxByOutboxConfirmRetry(
          this.client,
          request,
        )
      : await postApiV1DocumentWorkflowMailOutboxByOutboxRetry(
          this.client,
          request,
        );
    return response.data;
  }

  async cancelOutbox(
    id: string,
    reason: string,
  ): Promise<DocumentWorkflowOutbox> {
    return (
      await postApiV1DocumentWorkflowMailOutboxByOutboxCancel(this.client, {
        path: { outbox: id },
        body: { reason },
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async linkMessage(messageId: string, documentId: string) {
    return (
      await postApiV1DocumentWorkflowMailMessagesByMessageDocumentLinks(
        this.client,
        {
          path: { message: messageId },
          body: { document_id: documentId },
          headers: idempotencyHeaders(),
        },
      )
    ).data;
  }

  async unlinkMessage(messageId: string, documentId: string) {
    await deleteApiV1DocumentWorkflowMailMessagesByMessageDocumentLinksByDocument(
      this.client,
      {
        path: { message: messageId, document: documentId },
        headers: idempotencyHeaders(),
      },
    );
  }

  async mergeThread(
    targetThreadId: string,
    sourceThreadId: string,
    reason: string,
  ) {
    return (
      await postApiV1DocumentWorkflowMailThreadsByThreadMerge(this.client, {
        path: { thread: targetThreadId },
        body: { source_thread_id: sourceThreadId, reason },
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async splitThread(threadId: string, messageIds: string[], reason: string) {
    return (
      await postApiV1DocumentWorkflowMailThreadsByThreadSplit(this.client, {
        path: { thread: threadId },
        body: { message_ids: messageIds, reason },
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async markRead(documentId: string) {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentRead(this.client, {
        path: { document: documentId },
      })
    ).data;
  }

  async compose(
    mailboxId: string,
    input: PostApiV1DocumentWorkflowMailboxesByMailboxMessagesBody,
  ) {
    return (
      await postApiV1DocumentWorkflowMailboxesByMailboxMessages(this.client, {
        path: { mailbox: mailboxId },
        body: input,
        headers: idempotencyHeaders(),
      })
    ).data;
  }

  async reply(
    documentId: string,
    input: PostApiV1DocumentWorkflowDocumentsByDocumentRepliesBody,
    idempotencyKey?: string,
  ) {
    return (
      await postApiV1DocumentWorkflowDocumentsByDocumentReplies(this.client, {
        path: { document: documentId },
        body: input,
        headers: idempotencyHeaders(idempotencyKey),
      })
    ).data;
  }
}
